import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { parseHiddenModules, type OptionalModule } from '@/lib/modules'

export interface ModuleVisibility {
  /**
   * True only once settings have resolved and the module is shown. Use it to
   * decide whether to RENDER a module's UI: a deployment that hides the module
   * never sees it flash in while settings load.
   */
  isEnabled: (m: OptionalModule) => boolean
  /**
   * True only once settings have resolved and the module is hidden. Use it to
   * decide whether to REDIRECT away: a full deployment is never bounced off a
   * page just because settings are still loading.
   */
  isHidden: (m: OptionalModule) => boolean
}

/**
 * Which optional admin modules this deployment shows — see lib/modules.ts.
 * Shares the `['settings','all']` query cache, so it adds no network call. If
 * settings fail to load the modules count as shown: a flaky request must not
 * strip sections from a deployment that paid for them.
 */
export function useModuleVisibility(): ModuleVisibility {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  const hidden = parseHiddenModules(data?.data)
  return {
    isEnabled: (m) => !isLoading && !hidden.has(m),
    isHidden: (m) => !isLoading && hidden.has(m),
  }
}
